import { useEffect, useRef, useCallback, useMemo } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from '../../context/GoogleMapsContext';

const MAP_OPTIONS = {
  disableDefaultUI: true,
  zoomControl: true,
  styles: [
    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  ],
};

const DEFAULT_CENTER = { lat: 39.9334, lng: 32.8597 }; // Ankara

/**
 * MapPicker
 *
 * Props:
 *   geocodeHint  — string: il+ilçe (değişince otomatik geocode tetiklenir)
 *   latitude     — number | ''
 *   longitude    — number | ''
 *   onChange     — ({ latitude, longitude }) => void
 */
export function MapPicker({ geocodeHint, latitude, longitude, onChange }) {
  const { isLoaded } = useGoogleMaps();
  const geocodingRef   = useRef(false);
  const geocoderRef    = useRef(null);
  const debounceRef    = useRef(null);

  // If coords already exist on mount, initialize lastHintRef to the current hint
  // so we don't re-geocode an already-pinned location (edit mode).
  const initialHint = useMemo(() => {
    return (latitude && longitude) ? (geocodeHint || '') : '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally only on mount
  const lastHintRef = useRef(initialHint);

  const geocode = useCallback(async (hint) => {
    if (!hint || hint === lastHintRef.current || geocodingRef.current) return;
    if (!window.google) return;

    lastHintRef.current = hint;
    geocodingRef.current = true;

    if (!geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }

    try {
      const result = await geocoderRef.current.geocode({
        address: `${hint}, Türkiye`,
        componentRestrictions: { country: 'TR' },
      });
      if (result.results[0]) {
        const loc = result.results[0].geometry.location;
        onChange({ latitude: loc.lat(), longitude: loc.lng() });
      }
    } catch {
      // network hatası sessizce geç
    } finally {
      geocodingRef.current = false;
    }
  }, [onChange]);

  useEffect(() => {
    if (!isLoaded || !geocodeHint) return;
    if (geocodeHint === lastHintRef.current) return;

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => geocode(geocodeHint), 600);

    return () => clearTimeout(debounceRef.current);
  }, [isLoaded, geocodeHint, geocode]);

  const lat    = latitude  ? parseFloat(latitude)  : null;
  const lng    = longitude ? parseFloat(longitude) : null;
  const hasPin = lat && lng && isFinite(lat) && isFinite(lng);
  const center = hasPin ? { lat, lng } : DEFAULT_CENTER;

  const handleMapClick = useCallback((e) => {
    onChange({ latitude: e.latLng.lat(), longitude: e.latLng.lng() });
  }, [onChange]);

  const handleDragEnd = useCallback((e) => {
    onChange({ latitude: e.latLng.lat(), longitude: e.latLng.lng() });
  }, [onChange]);

  if (!isLoaded) {
    return (
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-medium text-slate-700">Konum</label>
        </div>
        <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center" style={{ height: 220 }}>
          <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-medium text-slate-700">Konum</label>
        {hasPin ? (
          <span className="text-xs text-slate-500">
            {lat.toFixed(5)}, {lng.toFixed(5)}
          </span>
        ) : (
          <span className="text-xs text-slate-400">İl/ilçe seçince pin otomatik eklenir</span>
        )}
      </div>
      <div className="rounded-xl overflow-hidden border border-slate-200" style={{ height: 220 }}>
        <GoogleMap
          mapContainerStyle={{ height: '100%', width: '100%' }}
          center={center}
          zoom={hasPin ? 13 : 6}
          options={{ ...MAP_OPTIONS, scrollwheel: false }}
          onClick={handleMapClick}
        >
          {hasPin && (
            <Marker
              position={{ lat, lng }}
              draggable
              onDragEnd={handleDragEnd}
            />
          )}
        </GoogleMap>
      </div>
      <p className="text-xs text-slate-400 mt-1">Pini sürükle veya haritaya tıkla konumu ayarla</p>
    </div>
  );
}
